import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Alert, Button, Spinner, Table, Modal, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faSearch } from '@fortawesome/free-solid-svg-icons';
import { InternalUserAPI } from '../../API/InternalUserAPI';
import CardHeader from '../../Components/Card/CardHeader';
import GridContainer from '../../Components/Grid/GridContainer';

interface State {
    users: any[];
    tls: any[];
    loading: boolean;
    error: string | null;
    showModal: boolean;
    modalMode: 'CREATE' | 'UPDATE';
    selectedUser: any;
    formData: {
        email: string;
        password?: string;
        role: string;
        managerId: string;
    };
    saving: boolean;
    search: string;
    roleFilter: string;
}

const ROLE_LABELS: { [role: string]: string } = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    tl: 'Team Leader',
    pm: 'Project Manager',
    sales: 'Sales',
    finance: 'Finance',
};

const ROLE_COLORS: { [role: string]: { bg: string; fg: string } } = {
    super_admin: { bg: '#fde8e8', fg: '#a61b1b' },
    admin: { bg: '#fff1e0', fg: '#9a4d00' },
    tl: { bg: '#e6f0ff', fg: '#1d4ed8' },
    pm: { bg: '#e7f6ec', fg: '#17703a' },
    sales: { bg: '#f3e8ff', fg: '#6b21a8' },
    finance: { bg: '#e6f7f7', fg: '#0f6b6b' },
};

const RoleBadge = ({ role }: { role: string }) => {
    const c = ROLE_COLORS[role] || { bg: '#eef0f3', fg: '#444' };
    return (
        <span style={{ background: c.bg, color: c.fg, padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {ROLE_LABELS[role] || role}
        </span>
    );
};

class UserManagement extends Component<any, State> {
    constructor(props: any) {
        super(props);
        this.state = {
            users: [],
            tls: [],
            loading: true,
            error: null,
            showModal: false,
            modalMode: 'CREATE',
            selectedUser: null,
            formData: {
                email: '',
                password: '',
                role: 'pm',
                managerId: ''
            },
            saving: false,
            search: '',
            roleFilter: ''
        };
    }

    componentDidMount() {
        this.fetchUsers();
    }

    fetchUsers = async () => {
        this.setState({ loading: true, error: null });
        try {
            const response = await InternalUserAPI.getInternalUsers();
            if (response && response.status === 1) {
                this.setState({
                    users: response.data.users || [],
                    tls: response.data.tls || [],
                    loading: false
                });
            } else {
                this.setState({ error: response.message || 'Failed to fetch users', loading: false });
            }
        } catch (error: any) {
            this.setState({ error: error.message || 'An error occurred', loading: false });
        }
    }

    openCreateModal = () => {
        this.setState({
            showModal: true,
            modalMode: 'CREATE',
            selectedUser: null,
            formData: { email: '', password: '', role: 'pm', managerId: '' }
        });
    }

    openEditModal = (user: any) => {
        this.setState({
            showModal: true,
            modalMode: 'UPDATE',
            selectedUser: user,
            formData: {
                email: user.email,
                role: user.role,
                managerId: user.managerId || ''
            }
        });
    }

    // Mirrors the backend: Super Admin is never editable here, and only a
    // Super Admin edits an Admin.
    canEditUser = (user: any) => {
        if (user.role === 'super_admin') return false;
        if (user.role === 'admin' && this.props.role !== 'super_admin') return false;
        return true;
    }

    isSelf = (user: any) => user && String(user.id) === String(this.props.userId);

    closeModal = () => {
        this.setState({ showModal: false });
    }

    handleInputChange = (e: any) => {
        const { name, value } = e.target;
        this.setState(prevState => ({
            formData: {
                ...prevState.formData,
                [name]: value
            }
        }));
    }

    handleSubmit = async (e: any) => {
        e.preventDefault();
        this.setState({ saving: true });
        try {
            let response;
            if (this.state.modalMode === 'CREATE') {
                response = await InternalUserAPI.createInternalUser(this.state.formData);
            } else {
                response = await InternalUserAPI.updateInternalUser(this.state.selectedUser.id, this.state.formData);
            }

            if (response && response.status === 1) {
                Swal.fire('Success', `User successfully ${this.state.modalMode === 'CREATE' ? 'created' : 'updated'}`, 'success');
                this.closeModal();
                this.fetchUsers();
            } else {
                Swal.fire('Error', response.message || 'An error occurred', 'error');
            }
        } catch (error: any) {
            Swal.fire('Error', error.message || 'An error occurred', 'error');
        } finally {
            this.setState({ saving: false });
        }
    }

    renderModal = () => {
        const { showModal, modalMode, formData, tls, saving } = this.state;
        const needsManager = formData.role === 'pm';

        return (
            <Modal show={showModal} onHide={this.closeModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{modalMode === 'CREATE' ? 'Create Internal User' : 'Edit User Role/Assignment'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={this.handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control 
                                type="email" 
                                name="email" 
                                value={formData.email} 
                                onChange={this.handleInputChange} 
                                required 
                                disabled={modalMode === 'UPDATE'} 
                            />
                        </Form.Group>
                        {modalMode === 'CREATE' && (
                            <Form.Group className="mb-3">
                                <Form.Label>Password</Form.Label>
                                <Form.Control 
                                    type="password" 
                                    name="password" 
                                    value={formData.password} 
                                    onChange={this.handleInputChange} 
                                    required={modalMode === 'CREATE'} 
                                />
                            </Form.Group>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>Role</Form.Label>
                            <Form.Control
                                as="select"
                                name="role"
                                value={formData.role}
                                onChange={this.handleInputChange}
                                disabled={modalMode === 'UPDATE' && this.isSelf(this.state.selectedUser)}
                            >
                                {this.props.role === 'super_admin' && <option value="admin">Admin</option>}
                                <option value="tl">Team Leader (TL)</option>
                                <option value="pm">Project Manager (PM)</option>
                                <option value="sales">Sales</option>
                                <option value="finance">Finance</option>
                            </Form.Control>
                        </Form.Group>
                        {needsManager && (
                            <Form.Group className="mb-3">
                                <Form.Label>Assign to Team Leader</Form.Label>
                                <Form.Control as="select" name="managerId" value={formData.managerId} onChange={this.handleInputChange}>
                                    <option value="">-- No Manager --</option>
                                    {tls.map(tl => (
                                        <option key={tl.id} value={tl.id}>
                                            {tl.username ? `${tl.username} (${tl.email})` : tl.email}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.closeModal}>Cancel</Button>
                        <Button variant="primary" type="submit" disabled={saving}>
                            {saving ? <Spinner size="sm" animation="border" /> : 'Save User'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        );
    }

    render() {
        const { users, tls, loading, error, search, roleFilter } = this.state;
        const isTl = this.props.role === 'tl';
        const q = search.trim().toLowerCase();
        const visibleUsers = users.filter(u =>
            (!roleFilter || u.role === roleFilter) &&
            (!q || String(u.username || '').toLowerCase().includes(q) || String(u.email || '').toLowerCase().includes(q))
        );
        const roleCounts = users.reduce((acc: { [r: string]: number }, u) => {
            acc[u.role] = (acc[u.role] || 0) + 1;
            return acc;
        }, {});
        const colCount = isTl ? 3 : 4;

        return (
            <div className="content">
                <GridContainer>
                    <div className="w-100 mb-3">
                        <CardHeader color="primary">
                            <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 12 }}>
                                <div>
                                    <h4 className="mb-0 text-white">Internal User Management</h4>
                                    <small className="text-white-50">
                                        {isTl ? 'Your team members' : 'Staff accounts, roles and team assignments'}
                                    </small>
                                </div>
                                {!isTl && (
                                    <Button variant="light" size="sm" onClick={this.openCreateModal}>
                                        <FontAwesomeIcon icon={faPlus} className="mr-2" /> Create User
                                    </Button>
                                )}
                            </div>
                        </CardHeader>
                    </div>
                </GridContainer>

                {error && <Alert variant="danger" className="mt-2">{error}</Alert>}

                <div className="bg-white border shadow-sm rounded p-3 mb-3">
                    <div className="d-flex flex-wrap align-items-center" style={{ gap: 12 }}>
                        <div className="position-relative" style={{ flex: '1 1 260px', maxWidth: 360 }}>
                            <FontAwesomeIcon
                                icon={faSearch}
                                style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#999' }}
                            />
                            <Form.Control
                                type="search"
                                placeholder="Search by username or email"
                                value={search}
                                onChange={(e: any) => this.setState({ search: e.target.value })}
                                style={{ paddingLeft: 34 }}
                            />
                        </div>
                        <Form.Control
                            as="select"
                            value={roleFilter}
                            onChange={(e: any) => this.setState({ roleFilter: e.target.value })}
                            style={{ flex: '0 1 200px' }}
                        >
                            <option value="">All roles ({users.length})</option>
                            {Object.keys(ROLE_LABELS).filter(r => roleCounts[r]).map(r => (
                                <option key={r} value={r}>{ROLE_LABELS[r]} ({roleCounts[r]})</option>
                            ))}
                        </Form.Control>
                        <span className="text-muted ml-auto" style={{ fontSize: 13 }}>
                            Showing {visibleUsers.length} of {users.length}
                        </span>
                    </div>
                </div>

                <div className="bg-white border shadow-sm rounded">
                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" /></div>
                    ) : (
                        <Table hover responsive className="mb-0" style={{ fontSize: 14 }}>
                            <thead style={{ background: '#f7f8fa' }}>
                                <tr>
                                    <th style={{ borderTop: 0 }}>User</th>
                                    <th style={{ borderTop: 0 }}>Role</th>
                                    <th style={{ borderTop: 0 }}>Team Leader</th>
                                    {!isTl && <th style={{ borderTop: 0 }} className="text-right">Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {visibleUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan={colCount} className="text-center text-muted py-5">
                                            {users.length === 0 ? 'No internal users found.' : 'No users match your search.'}
                                        </td>
                                    </tr>
                                ) : (
                                    visibleUsers.map(user => {
                                        // Only a PM reports to a TL; any other role shows no manager.
                                        const manager = user.role === 'pm' ? tls.find(t => t.id === user.managerId) : null;
                                        const canEdit = this.canEditUser(user);
                                        return (
                                            <tr key={user.id}>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    <div style={{ fontWeight: 600 }}>
                                                        {user.username || '-'}
                                                        {this.isSelf(user) && <span className="text-muted ml-2" style={{ fontWeight: 400, fontSize: 12 }}>(you)</span>}
                                                    </div>
                                                    <div className="text-muted" style={{ fontSize: 13 }}>{user.email}</div>
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}><RoleBadge role={user.role} /></td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    {manager ? (
                                                        <>
                                                            <div>{manager.username || manager.email}</div>
                                                            {manager.username && <div className="text-muted" style={{ fontSize: 13 }}>{manager.email}</div>}
                                                        </>
                                                    ) : (
                                                        <span className="text-muted">{user.role === 'pm' ? 'Unassigned' : '—'}</span>
                                                    )}
                                                </td>
                                                {!isTl && (
                                                    <td className="text-right" style={{ verticalAlign: 'middle' }}>
                                                        {canEdit ? (
                                                            <Button variant="outline-primary" size="sm" onClick={() => this.openEditModal(user)}>
                                                                <FontAwesomeIcon icon={faEdit} className="mr-1" /> Edit
                                                            </Button>
                                                        ) : (
                                                            <span className="text-muted" style={{ fontSize: 12 }}>Protected</span>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    )}
                </div>
                {!isTl && this.renderModal()}
            </div>
        );
    }
}

function mapStateToProps(state: any) {
    const u = state.adminUser?.adminUser || {};
    return {
        role: u.role,
        userId: u.userId,
    };
}

export default connect(mapStateToProps)(UserManagement);
